import React, { useState } from 'react';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';


const ComparisonTool = ({ stateRegulations, selectedStates: initialStates }) => {
  const [comparisonStates, setComparisonStates] = useState(initialStates?.slice(0, 3) || []);

  const availableStates = Object.keys(stateRegulations)?.map(code => ({
    value: code,
    label: stateRegulations?.[code]?.name
  }));

  const addStateToComparison = (stateCode) => {
    if (comparisonStates?.length < 3 && !comparisonStates?.includes(stateCode)) {
      setComparisonStates([...comparisonStates, stateCode]);
    }
  };

  const removeStateFromComparison = (stateCode) => {
    setComparisonStates(comparisonStates?.filter(s => s !== stateCode));
  };

  const comparisonFields = [
    { key: 'weightLimit', label: 'Weight Limit', icon: 'Weight' },
    { key: 'licenseRequirements', label: 'License Requirements', icon: 'CreditCard' },
    { key: 'speedRestrictions', label: 'Speed Restrictions', icon: 'Gauge' },
    { key: 'brakeRequirements', label: 'Brake Requirements', icon: 'CircleStop' },
    { key: 'lightingRequirements', label: 'Lighting Requirements', icon: 'Lightbulb' },
    { key: 'safetyChains', label: 'Safety Chains', icon: 'Link' }
  ];

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="GitCompare" size={24} className="text-primary" strokeWidth={2} />
          <h2 className="font-heading text-h3 text-foreground">State Comparison Tool</h2>
        </div>
        
        {comparisonStates?.length < 3 && (
          <div className="w-48">
            <Select
              options={availableStates?.filter(s => !comparisonStates?.includes(s?.value))}
              value=""
              onChange={addStateToComparison}
              placeholder="Add state..."
            />
          </div>
        )}
      </div>

      {comparisonStates?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="GitCompare" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-body text-muted-foreground">Select states to compare regulations side-by-side</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-heading font-semibold text-foreground p-3">Regulation</th>
                  {comparisonStates?.map(stateCode => (
                    <th key={stateCode} className="text-left font-heading font-semibold text-foreground p-3">
                      <div className="flex items-center justify-between">
                        <span>{stateRegulations?.[stateCode]?.name}</span>
                        <button
                          onClick={() => removeStateFromComparison(stateCode)}
                          className="p-1 hover:bg-muted rounded transition-smooth"
                          aria-label="Remove state"
                        >
                          <Icon name="X" size={16} strokeWidth={2} className="text-muted-foreground" />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFields?.map(field => (
                  <tr key={field?.key} className="border-b border-border hover:bg-muted/50 transition-smooth">
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <Icon name={field?.icon} size={16} strokeWidth={2} className="text-primary" />
                        <span className="font-body font-medium text-foreground">{field?.label}</span>
                      </div>
                    </td>
                    {comparisonStates?.map(stateCode => (
                      <td key={stateCode} className="p-3 font-body text-sm text-muted-foreground">
                        {stateRegulations?.[stateCode]?.[field?.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-4">
            {comparisonStates?.map(stateCode => (
              <div key={stateCode} className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-foreground">
                    {stateRegulations?.[stateCode]?.name}
                  </h3>
                  <button
                    onClick={() => removeStateFromComparison(stateCode)}
                    className="p-1 hover:bg-muted rounded transition-smooth"
                    aria-label="Remove state"
                  >
                    <Icon name="X" size={16} strokeWidth={2} className="text-muted-foreground" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  {comparisonFields?.map(field => (
                    <div key={field?.key} className="flex items-start space-x-2">
                      <Icon name={field?.icon} size={16} strokeWidth={2} className="text-primary mt-1" />
                      <div>
                        <p className="font-body text-xs font-medium text-foreground">{field?.label}</p>
                        <p className="font-body text-xs text-muted-foreground">
                          {stateRegulations?.[stateCode]?.[field?.key]}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonTool;